import React from 'react';
import styled from 'styled-components';

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

const DEADLINES = {
    0: [ // January
        { day: 31, text: 'Self Assessment Tax Return', overdue: true },
        { day: 19, text: 'PAYE/NIC Monthly Submission', overdue: true },
        { day: 7, text: 'VAT Return (Q4)', overdue: true },
    ],
    1: [ // February
        { day: 19, text: 'PAYE/NIC Monthly Submission', overdue: true },
        { day: 2, text: 'P46(Car) Quarterly Return', overdue: true },
    ],
    2: [ // March
        { day: 19, text: 'PAYE/NIC Monthly Submission', overdue: true },
        { day: 31, text: 'Corporation Tax Payment Due', overdue: true },
        { day: 31, text: 'End of Tax Year Prep', overdue: true },
    ],
    3: [ // April
        { day: 5, text: 'End of Tax Year 2024/25', overdue: true },
        { day: 19, text: 'PAYE/NIC Monthly Submission', overdue: true },
        { day: 30, text: 'MTD Quarterly Update (Q1)', overdue: true },
    ],
    4: [ // May
        { day: 3, text: 'P46(Car) Quarterly Return', overdue: true },
        { day: 19, text: 'PAYE/NIC Monthly Submission', overdue: true },
        { day: 31, text: 'Auto Enrolment Pension Review', overdue: true },
    ],
    5: [ // June
        { day: 19, text: 'PAYE/NIC Monthly Submission', overdue: true },
        { day: 30, text: 'Companies House Annual Return', overdue: true },
    ],
    6: [ // July
        { day: 6, text: 'P11D / P11D(b) Submission', overdue: true },
        { day: 7, text: 'VAT Return (Q1)', overdue: true },
        { day: 19, text: 'PAYE/NIC Monthly Submission', overdue: true },
        { day: 31, text: 'MTD Quarterly Update (Q2)', overdue: true },
    ],
    7: [ // August
        { day: 2, text: 'P46(Car) Quarterly Return', overdue: true },
        { day: 19, text: 'PAYE Settlement Agreement', overdue: true },
    ],
    8: [ // September
        { day: 19, text: 'PAYE/NIC Monthly Submission', overdue: true },
        { day: 30, text: 'Corporation Tax CT600 Filing', overdue: true },
    ],
    9: [ // October
        { day: 5, text: 'Self Assessment Registration', overdue: true },
        { day: 7, text: 'VAT Return (Q2)', overdue: true },
        { day: 19, text: 'CIS Monthly Return', overdue: true },
        { day: 31, text: 'MTD Quarterly Update (Q3)', overdue: true },
    ],
    10: [ // November
        { day: 2, text: 'P46(Car) Quarterly Return', overdue: true },
        { day: 19, text: 'PAYE/NIC Monthly Submission', overdue: true },
    ],
    11: [ // December
        { day: 19, text: 'PAYE/NIC Monthly Submission', overdue: true },
        { day: 31, text: 'Corporation Tax Payment', overdue: true },
        { day: 30, text: 'Self Assessment Online Deadline Approaches', overdue: false },
    ],
};

function ComplianceCalendar({ onClose }) {
    const monthIndex = new Date().getMonth();
    const currentDeadlines = DEADLINES[monthIndex] || [];
    const daysInMonth = new Date(2025, monthIndex + 1, 0).getDate();
    const firstDay = new Date(2025, monthIndex, 1).getDay();
    const deadlineDays = new Set(currentDeadlines.filter(d => d.overdue).map(d => d.day));

    const cells = [];
    for (let i = 0; i < firstDay; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(d);

    // Calculate overdue count across all months
    const totalOverdue = Object.values(DEADLINES).flat().filter(d => d.overdue).length;

    return (
        <Div>
            <div className="cal__toolbar">
                <span className="cal__toolbar__title">Compliance Calendar - Tax Year 2024/25</span>
            </div>
            <div className="cal__alert">
                <span className="cal__alert__icon">!</span>
                <span>{totalOverdue} compliance deadlines overdue or at risk across all months</span>
            </div>
            <div className="cal__main">
                <div className="cal__month">
                    <div className="cal__month__title">{MONTHS[monthIndex]} 2025</div>
                    <div className="cal__grid">
                        {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(d => (
                            <div key={d} className="cal__cell cal__cell--header">{d}</div>
                        ))}
                        {cells.map((day, i) => (
                            <div key={i} className={`cal__cell ${day ? '' : 'cal__cell--empty'} ${deadlineDays.has(day) ? 'cal__cell--overdue' : ''}`}>
                                {day || ''}
                            </div>
                        ))}
                    </div>
                </div>
                <div className="cal__deadlines">
                    <div className="cal__deadlines__title">This Month's Deadlines</div>
                    {currentDeadlines.map((dl, i) => (
                        <div key={i} className={`cal__deadline ${dl.overdue ? 'cal__deadline--overdue' : ''}`}>
                            <span className="cal__deadline__day">{dl.day}</span>
                            <span className="cal__deadline__text">{dl.text}</span>
                            {dl.overdue && <span className="cal__deadline__badge">OVERDUE</span>}
                        </div>
                    ))}
                    <div className="cal__deadlines__all">
                        <div className="cal__deadlines__title">Annual Overview</div>
                        <div className="cal__deadline">
                            <span className="cal__deadline__text">PAYE/NIC: Monthly on 19th (all months overdue)</span>
                        </div>
                        <div className="cal__deadline">
                            <span className="cal__deadline__text">VAT Returns: Quarterly (Q1-Q4 all overdue)</span>
                        </div>
                        <div className="cal__deadline">
                            <span className="cal__deadline__text">MTD Quarterly Updates: 4 per year (all overdue)</span>
                        </div>
                        <div className="cal__deadline">
                            <span className="cal__deadline__text">CIS Returns: Monthly on 19th</span>
                        </div>
                        <div className="cal__deadline">
                            <span className="cal__deadline__text">P11D: 6 July annually</span>
                        </div>
                        <div className="cal__deadline">
                            <span className="cal__deadline__text">Self Assessment: 31 January</span>
                        </div>
                    </div>
                </div>
            </div>
            <div className="cal__footer">
                <div className="cal__cta">
                    Stop missing deadlines. Stop the HMRC penalties.
                </div>
                <div className="cal__cta__bold">Dead Simple Accounting: 0118 359 3666</div>
                <div className="cal__cta__sub">VAT | PAYE | Payroll | Corporation Tax | Self Assessment | Making Tax Digital</div>
                <div className="cal__cta__web">deadsimpleaccounting.co.uk</div>
            </div>
        </Div>
    );
}

const Div = styled.div`
  height: 100%;
  width: 100%;
  position: absolute;
  display: flex;
  flex-direction: column;
  background: #ece9d8;
  font-family: Tahoma, sans-serif;
  font-size: 11px;

  .cal__toolbar {
    background: linear-gradient(to bottom, #fafafa, #e4e4e4);
    padding: 4px 8px;
    font-size: 11px;
    font-weight: bold;
    border-bottom: 1px solid #aaa;
  }
  .cal__alert {
    background: #ffcccc;
    border: 1px solid #cc0000;
    color: #cc0000;
    margin: 6px;
    padding: 6px 10px;
    border-radius: 2px;
    font-weight: bold;
    font-size: 11px;
    display: flex;
    align-items: center;
    gap: 8px;
  }
  .cal__alert__icon {
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background: #cc0000;
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 12px;
    font-weight: bold;
    flex-shrink: 0;
  }
  .cal__main {
    flex: 1;
    display: flex;
    gap: 8px;
    margin: 0 6px;
    overflow: hidden;
  }
  .cal__month {
    width: 210px;
    flex-shrink: 0;
    border: 1px solid #7f9db9;
    background: white;
  }
  .cal__month__title {
    background: linear-gradient(to bottom, #4060b0, #2a4080);
    color: white;
    text-align: center;
    padding: 4px;
    font-weight: bold;
    font-size: 12px;
  }
  .cal__grid {
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    gap: 1px;
    padding: 4px;
  }
  .cal__cell {
    text-align: center;
    padding: 3px 2px;
    font-size: 10px;
    border-radius: 2px;
  }
  .cal__cell--header {
    font-weight: bold;
    color: #333;
    font-size: 9px;
  }
  .cal__cell--empty {
    background: transparent;
  }
  .cal__cell--overdue {
    background: #cc0000;
    color: white;
    font-weight: bold;
    border-radius: 2px;
  }
  .cal__deadlines {
    flex: 1;
    border: 1px solid #7f9db9;
    background: white;
    overflow-y: auto;
    padding: 8px;
  }
  .cal__deadlines__title {
    font-weight: bold;
    font-size: 11px;
    color: #003399;
    margin-bottom: 6px;
    padding-bottom: 3px;
    border-bottom: 1px solid #ddd;
  }
  .cal__deadline {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 3px 0;
    font-size: 10px;
    border-bottom: 1px solid #f0f0f0;
  }
  .cal__deadline--overdue {
    color: #cc0000;
  }
  .cal__deadline__day {
    width: 22px;
    font-weight: bold;
    text-align: right;
    flex-shrink: 0;
  }
  .cal__deadline__text {
    flex: 1;
  }
  .cal__deadline__badge {
    background: #cc0000;
    color: white;
    font-size: 8px;
    font-weight: bold;
    padding: 1px 4px;
    border-radius: 2px;
  }
  .cal__deadlines__all {
    margin-top: 10px;
    padding-top: 6px;
  }
  .cal__footer {
    padding: 6px 8px;
    text-align: center;
    border-top: 1px solid #aaa;
    background: linear-gradient(to bottom, #f5f5f0, #e8e8e0);
  }
  .cal__cta {
    font-size: 10px;
    color: #cc0000;
    font-weight: bold;
  }
  .cal__cta__bold {
    font-size: 13px;
    font-weight: bold;
    color: #003399;
  }
  .cal__cta__sub {
    font-size: 9px;
    color: #666;
  }
  .cal__cta__web {
    font-size: 10px;
    color: #0066cc;
  }
`;

export default ComplianceCalendar;
